// frontend/src/features/forms/components/DynamicFormRenderer.tsx
// AM-006: پشتیبانی از header/body/footer و فیلد repeater
import type { FormDefinitionJson, FormFieldDef, FormSection } from "@/shared/utils/formSchema";

interface Props {
  definition: FormDefinitionJson;
  value: Record<string, any>;
  onChange: (key: string, value: any) => void;
  readOnly?: boolean;
}

function FieldInput({
  field, value, onChange, readOnly,
}: { field: FormFieldDef; value: any; onChange: (v: any) => void; readOnly?: boolean }) {
  if (field.type === "textarea") {
    return (
      <textarea className="input-field" rows={3} disabled={readOnly}
        value={value ?? ""} onChange={(e) => onChange(e.target.value)} />
    );
  }
  if (field.type === "select") {
    return (
      <select className="input-field" dir="rtl" disabled={readOnly}
        value={value ?? ""} onChange={(e) => onChange(e.target.value)}>
        <option value="">-- انتخاب کنید --</option>
        {(field.options || []).map((opt) => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    );
  }
  if (field.type === "checkbox") {
    return (
      <input type="checkbox" disabled={readOnly}
        checked={!!value} onChange={(e) => onChange(e.target.checked)} />
    );
  }
  if (field.type === "repeater") {
    const rows: Record<string, any>[] = Array.isArray(value) ? value : [];
    const addRow = () => onChange([...rows, {}]);
    const removeRow = (idx: number) => onChange(rows.filter((_, i) => i !== idx));
    const updateCell = (idx: number, colKey: string, v: any) => {
      const next = rows.map((r, i) => (i === idx ? { ...r, [colKey]: v } : r));
      onChange(next);
    };
    return (
      <div className="table-container" style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", textAlign: "right", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              {(field.columns || []).map((c) => (
                <th key={c.key} style={{ padding: "0.5rem", fontSize: "0.85rem" }}>{c.label}</th>
              ))}
              {!readOnly && <th />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, idx) => (
              <tr key={idx}>
                {(field.columns || []).map((c) => (
                  <td key={c.key} style={{ padding: "0.4rem" }}>
                    <FieldInput field={c} value={row[c.key]} readOnly={readOnly}
                      onChange={(v) => updateCell(idx, c.key, v)} />
                  </td>
                ))}
                {!readOnly && (
                  <td>
                    <button type="button" className="btn btn-secondary" style={{ fontSize: "0.75rem" }}
                      onClick={() => removeRow(idx)}>حذف</button>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {!readOnly && (
          <button type="button" className="btn btn-secondary" style={{ marginTop: "0.5rem" }} onClick={addRow}>
            + افزودن ردیف
          </button>
        )}
      </div>
    );
  }
  return (
    <input type={field.type} className="input-field" disabled={readOnly}
      value={value ?? ""}
      onChange={(e) => onChange(field.type === "number" ? Number(e.target.value) : e.target.value)} />
  );
}

function SectionRenderer({
  section, value, onChange, readOnly, columns,
}: { section: FormSection; value: Record<string, any>; onChange: (k: string, v: any) => void; readOnly?: boolean; columns: 1 | 2 }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: columns === 2 ? "1fr 1fr" : "1fr", gap: "1.25rem" }}>
      {section.fields.map((field) => (
        <div className="form-group" key={field.key} style={field.type === "repeater" ? { gridColumn: "1 / -1" } : undefined}>
          <label className="form-label">
            {field.label} {field.required && <span style={{ color: "red" }}>*</span>}
          </label>
          <FieldInput field={field} value={value[field.key]} readOnly={readOnly}
            onChange={(v) => onChange(field.key, v)} />
          {field.help && (
            <p style={{ fontSize: "0.8rem", color: "var(--color-text-muted)", marginTop: "0.4rem" }}>{field.help}</p>
          )}
        </div>
      ))}
    </div>
  );
}

export function DynamicFormRenderer({ definition, value, onChange, readOnly }: Props) {
  const columns = definition.layout?.columns === 2 ? 2 : 1;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "2rem" }}>
      {definition.header.fields.length > 0 && (
        <div>
          <h3 className="font-bold mb-3" style={{ fontSize: "0.95rem", color: "var(--color-text-muted)" }}>هدر فرم</h3>
          <SectionRenderer section={definition.header} value={value} onChange={onChange} readOnly={readOnly} columns={columns} />
        </div>
      )}
      <div>
        <SectionRenderer section={definition.body} value={value} onChange={onChange} readOnly={readOnly} columns={columns} />
      </div>
      {definition.footer.fields.length > 0 && (
        <div style={{ borderTop: "1px solid var(--glass-border)", paddingTop: "1.5rem" }}>
          <h3 className="font-bold mb-3" style={{ fontSize: "0.95rem", color: "var(--color-text-muted)" }}>فوتر فرم</h3>
          <SectionRenderer section={definition.footer} value={value} onChange={onChange} readOnly={readOnly} columns={columns} />
          {definition.footer.signature_required && (
            <p style={{ marginTop: "1rem", fontSize: "0.85rem", color: "var(--color-gold)" }}>
              ⚠ این فرم نیاز به امضای دیجیتال دارد (مکانیزم امضا در نسخهٔ بعدی).
            </p>
          )}
        </div>
      )}
    </div>
  );
}
